import type { ColumnMeta } from "@/components/table-columns-menu";
import type { MonitoringEntry } from "../_data/types";

export const COLUMN_IDS = [
  "name",
  "identifier",
  "externalId",
  "type",
  "dob",
  "country",
  "dateAdded",
  "matches",
  "fp",
  "rp",
  "newCount",
] as const;
export type ColumnId = (typeof COLUMN_IDS)[number];

export type EntriesSortKey = ColumnId;
export type EntriesSort = { key: EntriesSortKey; dir: "asc" | "desc" } | null;

const STRING_KEYS: ReadonlySet<EntriesSortKey> = new Set([
  "name",
  "identifier",
  "externalId",
  "type",
  "country",
]);
const NULLABLE_DATE_KEYS: ReadonlySet<EntriesSortKey> = new Set(["dob"]);
const DATE_KEYS: ReadonlySet<EntriesSortKey> = new Set(["dateAdded"]);
const NUMBER_KEYS: ReadonlySet<EntriesSortKey> = new Set(["matches", "fp", "rp", "newCount"]);

export function sortEntries(entries: MonitoringEntry[], sort: EntriesSort): MonitoringEntry[] {
  if (!sort) return entries;
  const dir = sort.dir === "asc" ? 1 : -1;
  const key = sort.key;
  return [...entries].sort((a, b) => {
    if (STRING_KEYS.has(key)) {
      return dir * String(a[key]).localeCompare(String(b[key]));
    }
    if (NUMBER_KEYS.has(key)) {
      return dir * ((a[key] as number) - (b[key] as number));
    }
    if (NULLABLE_DATE_KEYS.has(key) || DATE_KEYS.has(key)) {
      const av = a[key] as string | null;
      const bv = b[key] as string | null;
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return dir * av.localeCompare(bv);
    }
    return 0;
  });
}

export const ENTRIES_LOCKED_FIRST: readonly ColumnId[] = ["name"];

const COLUMN_LABELS: Record<ColumnId, string> = {
  name: "Name",
  identifier: "Identifier",
  externalId: "External ID",
  type: "Type",
  dob: "DOB",
  country: "Country",
  dateAdded: "Date added",
  matches: "Matches",
  fp: "FP",
  rp: "RP",
  newCount: "New",
};

const REQUIRED_COLUMNS: ReadonlySet<ColumnId> = new Set<ColumnId>(["name"]);

export const ENTRIES_COLUMNS_META: readonly ColumnMeta[] = COLUMN_IDS.map((id) => ({
  id,
  label: COLUMN_LABELS[id],
  required: REQUIRED_COLUMNS.has(id),
}));
